// @dynamic
export class StringUtils {
  /**
   * Returns the mode (most frequently occurring item) from an array of strings.
   * It disregards null, undefined, and empty strings.
   */
  static getStringModeOrNull<T extends string>(items: readonly T[]): T | null {
    if (!items || !items?.length) return null;
    const modeCounts: Record<T, number> = {} as Record<T, number>;
    let maxFreq = 0;
    let mode: T | null = null;
    const lexicallyCompare = Intl.Collator('en', { numeric: true }).compare;
    for (const item of items || []) {
      if (!item) continue; // Skip null, undefined, and empty string items
      const count = (modeCounts[item] = (modeCounts[item] || 0) + 1);
      const larger = count > maxFreq;
      const same = count === maxFreq;
      const sameAndLexicallySmaller = same && (mode === null || lexicallyCompare(item, mode) < 0);
      if (larger || sameAndLexicallySmaller) {
        maxFreq = count;
        mode = item;
      }
    }
    return mode;
  }

  static removeWhiteSpace(s: string): string {
    return s?.replace(/\s/g, '');
  }

  static gridColumnComparisonString(unitString: string): string {
    return StringUtils.removeWhiteSpace(unitString?.toLowerCase());
  }

  static capitalize(s: string): string {
    return s.length > 1 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  }

  static sentenceCase(s: string): string {
    return StringUtils.capitalize(s?.toLowerCase());
  }

  static pascalCaseToKebabCase(s: string): string {
    return s
      ?.split(/(?=[A-Z])/)
      ?.join('-')
      ?.toLowerCase();
  }

  static pascalCaseToCamelCase(s: string): string {
    return s?.charAt(0)?.toLowerCase() + s?.slice(1);
  }

  static camelize(str): string {
    return str
      ?.replace(/^\w|[A-Z]|\b\w/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
      ?.replace(/\s+/g, '');
  }

  /**
   * Pascal case, or PascalCase, is a variable naming convention in programming in which the first letter of each word
   * is uppercase and the remaining letters are lowercase, with no spaces or other separators between words.
   */
  static toPascalCase(s: string, separator: string = ' ') {
    return s
      ?.split(separator)
      ?.map(word => StringUtils.capitalize(word))
      ?.join('');
  }

  /**
   * Splits PascalCase and preserves acronyms while also ensuring numbers are separated from letters.
   *
   * Breakdown of the regex:
   *
   *  1. `(?<=[a-z0-9])(?=[A-Z])` → Splits when a lowercase letter or digit is followed by an uppercase letter.
   *     • ✅ `"PascalCase"` → `"Pascal Case"`
   *     • ✅ `"JSONParser"` → `"JSON Parser"` (does not split `"JSON"`)
   *
   *  2. `(?<=[A-Z])(?=[A-Z][a-z])` → Prevents splitting consecutive uppercased unless the next letter is lowercase.
   *     • ✅ `"XMLHttpRequest"` → `"XML Http Request"`
   *     • ✅ `"HTTPRequest"` → `"HTTP Request"`
   *     • ✅ `"APIResponseData"` → `"API Response Data"`
   *
   *  3. `(?<=[a-zA-Z])(?=[0-9])` → Splits when a letter (uppercase or lowercase) is followed by a digit.
   *     • ✅ `"Delta3Carene"` → `"Delta 3 Carene"`
   *     • ✅ `"Version42Update"` → `"Version 42 Update"`
   *
   *  4. `(?<=[0-9])(?=[a-zA-Z])` → Splits when a digit is followed by a letter.
   *     • ✅ `"Model2024X"` → `"Model 2024 X"`
   *     • ✅ `"Alpha2Beta"` → `"Alpha 2 Beta"`
   */
  static splitPascalCasePreserveAcronyms(s: string, separator: string = ' '): string {
    return s
      ?.split(/(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])|(?<=[a-zA-Z])(?=[0-9])|(?<=[0-9])(?=[a-zA-Z])/)
      ?.join(separator);
  }
}
